/* eslint-disable react/no-array-index-key */
import React, { useCallback, useRef } from 'react';
import { Edit3, Loader, Plus, Trash2 } from 'react-feather';

import { useThumbnailUpload } from '@/Product/hooks/useThumbnailUpload';
import { ThumbnailUploadProps } from '@/shared/types/s3Types';

import { useProductFormStore } from '../../store/productFormStore';

const ThumbnailUpload: React.FC<ThumbnailUploadProps> = ({
  thumbnailUrls,
  onThumbnailsChange,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const { setS3ImageUrlArray } = useProductFormStore();

  // 스토어 동기화를 포함한 콜백 생성
  const syncedOnThumbnailsChange = useCallback(
    (urls: string[]) => {
      onThumbnailsChange(urls);
      setS3ImageUrlArray(urls);
    },
    [onThumbnailsChange, setS3ImageUrlArray],
  );

  // 동기화된 콜백으로 useThumbnailUpload 훅 사용
  const {
    uploadState,
    handleFileUpload,
    handleFileEdit,
    handleDelete,
    handleImageClick,
    setEditingIndex,
  } = useThumbnailUpload(thumbnailUrls, syncedOnThumbnailsChange);

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    editInputRef.current?.click();
  };

  const handleFileInputChange = (files: FileList | null) => {
    handleFileUpload(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditInputChange = (files: FileList | null) => {
    if (uploadState.editingIndex !== null) {
      handleFileEdit(files, uploadState.editingIndex);
    }
    if (editInputRef.current) {
      editInputRef.current.value = '';
    }
  };

  return (
    <div>
      <h2 className="text-base font-medium">Thumbnail</h2>
      <div className="mt-3 flex flex-wrap gap-3">
        {thumbnailUrls.map((url, index) => (
          <div
            key={index}
            className="group relative h-24 w-24 overflow-hidden rounded border-2 border-[#CECECE]"
          >
            <button
              type="button"
              className="h-full w-full cursor-pointer border-none bg-transparent object-cover p-0"
              onClick={() => handleImageClick(url)}
              aria-label={`View Thumbnail ${index + 1}`}
              tabIndex={0}
            >
              <img
                src={url}
                alt={`Thumbnail ${index + 1}`}
                className="pointer-events-none h-full w-full object-cover"
              />
            </button>

            {!disabled && (
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  className="rounded-full bg-white p-1 hover:bg-gray-100"
                  onClick={() => handleEditClick(index)}
                  title="수정"
                >
                  <Edit3 size={14} className="text-gray-600" />
                </button>
                <button
                  type="button"
                  className="rounded-full bg-white p-1 hover:bg-gray-100"
                  onClick={() => handleDelete(index)}
                  title="삭제"
                >
                  <Trash2 size={14} className="text-red-600" />
                </button>
              </div>
            )}
          </div>
        ))}

        {!disabled && (
          <label
            htmlFor="thumbnail-upload"
            className="flex h-24 w-24 cursor-pointer items-center justify-center rounded border-2 border-dashed border-[#CECECE] transition-colors hover:border-[#95699F]"
          >
            {uploadState.isLoading ? (
              <Loader size={16} className="animate-spin text-[#707070]" />
            ) : (
              <Plus size={16} className="text-[#707070]" />
            )}
          </label>
        )}
      </div>

      {uploadState.error && (
        <p className="mt-2 text-sm text-red-600">{uploadState.error}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        id="thumbnail-upload"
        className="hidden"
        accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.mp4,.mov,.avi,.wmv,.flv,.webm"
        onChange={(e) => handleFileInputChange(e.target.files)}
      />

      <input
        ref={editInputRef}
        type="file"
        className="hidden"
        accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.mp4,.mov,.avi,.wmv,.flv,.webm"
        onChange={(e) => handleEditInputChange(e.target.files)}
      />
    </div>
  );
};

export default ThumbnailUpload;
